import React from "react";
import EmcureModalShell from "../EmcureModalShell/EmcureModalShell";
import SqlResultTableBody from "./SqlResultTableBody";
import { getLoadMoreBatchSize } from "../../../utils/tablePaginationUtils";

const SpinnerIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin-icon" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CsvIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

function FullViewExportDropdown({
  messageId,
  sessionId,
  totalCount,
  getCsvExportOptions,
  onPickExportOption,
  isOpen,
  onToggle,
  isExportOptionLoading,
  disabled,
}) {
  return (
    <div className="table-export-dropdown-wrapper">
      <button
        type="button"
        onClick={onToggle}
        className="btn-export-csv"
        disabled={disabled || !messageId || !sessionId}
        title={!messageId ? "Export is available only for saved messages" : undefined}
      >
        <CsvIcon />
        Export CSV
      </button>
      {isOpen ? (
        <div className="table-export-dropdown-menu table-export-dropdown-menu--above">
          {(getCsvExportOptions ? getCsvExportOptions(totalCount) : []).map((opt) => (
            <button
              key={opt.exportType}
              type="button"
              className="table-export-dropdown-item"
              onClick={() => onPickExportOption?.(opt.exportType)}
              disabled={isExportOptionLoading?.(opt.exportType)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Full-view data table modal shared by ChatSection and AdminDashboard.
 *
 * @param {object|null} open — payload: { data, columns, sqlQuery?, hasMore?, totalCount?, messageId?, sessionId? }
 */
export default function SqlResultDataTableFullViewModal({
  open,
  onClose,
  variant,
  formatScalarForDisplay,
  admin,
  chat,
}) {
  if (!open) return null;

  const { data, columns } = open;

  if (variant === "admin") {
    const {
      getCsvExportOptions,
      onPickExportOption,
      isExportMenuOpen,
      onToggleExportMenu,
      isExportOptionLoading,
      messageId,
      sessionId,
      totalCount,
      exportDisabled,
      showLoadMore,
      loadMoreLoading,
      onLoadMore,
      loadMoreCount: loadMoreCountProp,
    } = admin || {};

    const loadMoreCount =
      loadMoreCountProp ?? getLoadMoreBatchSize(data.length, totalCount);

    return (
      <EmcureModalShell
        onClose={onClose}
        title="Data Table - Full View"
        bodyClassName="chart-modal-body chart-modal-body-table"
      >
        <>
          <SqlResultTableBody
            wrapperMode="modal"
            rawResults={data}
            columns={columns}
            formatScalarForDisplay={formatScalarForDisplay}
            renderFooter={null}
            sort={admin?.sort}
          />
          <div className="modal-footer">
            <div className="modal-footer-info">
              Showing {data.length.toLocaleString()} rows
              {totalCount != null && totalCount !== "" ? (
                <span> of {Number(totalCount).toLocaleString()} total</span>
              ) : null}
            </div>
            <div className="modal-footer-actions">
              <FullViewExportDropdown
                messageId={messageId}
                sessionId={sessionId}
                totalCount={totalCount}
                getCsvExportOptions={getCsvExportOptions}
                onPickExportOption={onPickExportOption}
                isOpen={isExportMenuOpen}
                onToggle={onToggleExportMenu}
                isExportOptionLoading={isExportOptionLoading}
                disabled={exportDisabled}
              />
              {showLoadMore ? (
                <button
                  type="button"
                  onClick={onLoadMore}
                  disabled={loadMoreLoading}
                  className="btn-table-export"
                >
                  {loadMoreLoading ? (
                    <>
                      <SpinnerIcon />
                      Loading...
                    </>
                  ) : (
                    <>
                      Load More ({loadMoreCount})
                      <ChevronDown />
                    </>
                  )}
                </button>
              ) : null}
            </div>
          </div>
        </>
      </EmcureModalShell>
    );
  }

  if (variant === "chat") {
    const {
      getCsvExportOptions,
      onPickExportOption,
      isExportMenuOpen,
      onToggleExportMenu,
      isExportOptionLoading,
      messageId,
      sessionId,
      showLoadMore,
      loadMoreLoading,
      onLoadMore,
      totalCount,
      loadMoreCount: loadMoreCountProp,
    } = chat || {};

    const loadMoreCount =
      loadMoreCountProp ?? getLoadMoreBatchSize(data.length, totalCount);

    return (
      <EmcureModalShell
        onClose={onClose}
        title="Data Table - Full View"
        bodyClassName="chart-modal-body chart-modal-body-table"
      >
        <>
          <SqlResultTableBody
            wrapperMode="modal"
            rawResults={data}
            columns={columns}
            formatScalarForDisplay={formatScalarForDisplay}
            renderFooter={null}
            sort={chat?.sort}
          />
          <div className="modal-footer">
            <div className="modal-footer-info">
              Showing {data.length.toLocaleString()} rows
              {totalCount ? <span> of {Number(totalCount).toLocaleString()} total</span> : null}
            </div>
            <div className="modal-footer-actions">
              <FullViewExportDropdown
                messageId={messageId}
                sessionId={sessionId}
                totalCount={totalCount}
                getCsvExportOptions={getCsvExportOptions}
                onPickExportOption={onPickExportOption}
                isOpen={isExportMenuOpen}
                onToggle={onToggleExportMenu}
                isExportOptionLoading={isExportOptionLoading}
                disabled={!messageId || !sessionId}
              />
              {showLoadMore ? (
                <button
                  type="button"
                  onClick={onLoadMore}
                  disabled={loadMoreLoading}
                  className="btn-table-export"
                >
                  {loadMoreLoading ? (
                    <>
                      <SpinnerIcon />
                      Loading...
                    </>
                  ) : (
                    <>
                      Load More ({loadMoreCount})
                      <ChevronDown />
                    </>
                  )}
                </button>
              ) : null}
            </div>
          </div>
        </>
      </EmcureModalShell>
    );
  }

  return null;
}
